import { HttpErrorResponse } from '@angular/common/http';
import type { CapacitorOptions } from '@sentry/capacitor';
import { isUUIDLike } from '@wilson/utils';

// Taken from the Capacitor options: @sentry/capacitor and @sentry/browser ship different
// @sentry/core versions, so the browser event types do not match `beforeSend` here.
type BeforeSend = NonNullable<CapacitorOptions['beforeSend']>;
export type ErrorEvent = Parameters<BeforeSend>[0];
type EventHint = Parameters<BeforeSend>[1];

/**
 * Share of expected client-side errors that is still sent. The real count is about 1 / rate times
 * higher; kept events carry a `sample_rate` tag so this stays visible in Sentry.
 */
export const EXPECTED_ERROR_SAMPLE_RATE = 0.1;

/**
 * HTTP statuses that are normal on a phone and not a bug by themselves:
 * - 0: no network, or the request was cancelled
 * - 401: the session expired, the auth flow handles it
 */
const EXPECTED_HTTP_STATUSES = [0, 401];

const HTTP_FAILURE_MESSAGE = /^Http failure response for (\S+): (\d+)/;
const SERVER_RETURNED_CODE_MESSAGE = /^Server returned code (\d+)/;

interface HttpErrorInfo {
  status: number;
  url: string | null;
}

/**
 * Manual `captureException` calls pass the HttpErrorResponse itself. The Angular error handler
 * first turns it into a message, so there the status is read from the message text.
 */
function getHttpErrorInfo(exception: unknown): HttpErrorInfo | null {
  if (exception instanceof HttpErrorResponse) {
    return { status: exception.status, url: exception.url };
  }

  const message =
    typeof exception === 'string'
      ? exception
      : exception instanceof Error
        ? exception.message
        : null;
  if (!message) {
    return null;
  }

  const httpFailure = HTTP_FAILURE_MESSAGE.exec(message);
  if (httpFailure) {
    return { status: Number(httpFailure[2]), url: httpFailure[1] };
  }

  const serverReturnedCode = SERVER_RETURNED_CODE_MESSAGE.exec(message);
  if (serverReturnedCode) {
    return { status: Number(serverReturnedCode[1]), url: null };
  }

  return null;
}

function isTimeoutError(exception: unknown): boolean {
  return exception instanceof Error && exception.name === 'TimeoutError';
}

function replaceUUIDSegments(path: string): string {
  return path
    .split('/')
    .map((segment) => (isUUIDLike(segment) ? ':id' : segment))
    .join('/');
}

function getNormalizedPath(url: string | null): string {
  if (!url) {
    return '';
  }

  try {
    return replaceUUIDSegments(new URL(url).pathname);
  } catch {
    return replaceUUIDSegments(url.split('?')[0]);
  }
}

function sampleAsWarning(event: ErrorEvent): ErrorEvent | null {
  if (Math.random() >= EXPECTED_ERROR_SAMPLE_RATE) {
    return null;
  }

  event.level = 'warning';
  event.tags = { ...event.tags, sample_rate: EXPECTED_ERROR_SAMPLE_RATE };

  return event;
}

/**
 * Sends only a sample of expected client-side errors (offline, expired session, request timeout),
 * as warnings. Everything else is sent unchanged.
 */
export function mobileBeforeSend(
  event: ErrorEvent,
  hint: EventHint,
): ErrorEvent | null {
  const exception = hint?.originalException;

  const httpError = getHttpErrorInfo(exception);
  if (httpError && EXPECTED_HTTP_STATUSES.includes(httpError.status)) {
    const sampledEvent = sampleAsWarning(event);
    if (sampledEvent) {
      sampledEvent.fingerprint = [
        'http',
        String(httpError.status),
        getNormalizedPath(httpError.url),
      ];
    }
    return sampledEvent;
  }

  if (isTimeoutError(exception)) {
    return sampleAsWarning(event);
  }

  return event;
}
